import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Spin, Splitter, Space, Typography } from 'antd';
import { FileTextOutlined, PartitionOutlined } from '@ant-design/icons';
import {
  remoteEditActions,
  TaxDocumentRecord,
  useListTaxDocumentsQuery,
} from '@/store/apis/taxDocumentsApi';
import { useGetTaxDealByIdQuery } from '@/store/apis/taxDealsApi';
import { useAppDispatch } from '@/hooks/reduxHooks';
import { subscribeTaxDocEdits } from '@/pages/tax/routes/TaxDocumentsRecord/sync/taxDocumentSync';
import TaxPreviewPanel from '../components/TaxPreviewPanel';
import TaxDocumentExplorer from '../components/TaxDocumentExplorer';
import ManagePropertiesDrawer from '../components/ManagePropertiesModal';

const { Text } = Typography;

const tokenize = (s: string): string[] =>
  s
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const matchesProperty = (
  ocrProp: string | null | undefined,
  dealAddr: string,
): boolean => {
  if (!ocrProp) return false;
  const aSet = new Set(tokenize(dealAddr));
  const bSet = new Set(tokenize(ocrProp));
  const overlap = [...aSet].filter((t) => bSet.has(t)).length;
  const shorter = Math.min(aSet.size, bSet.size);
  return shorter > 0 && overlap >= Math.ceil(shorter * 0.5);
};

const TaxDocumentTab: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const initialDocId = searchParams.get('docId');

  const { data: docs, isLoading } = useListTaxDocumentsQuery(dealId ?? '', {
    skip: !dealId,
  });

  useEffect(() => {
    if (!dealId) return;
    return subscribeTaxDocEdits((msg) => {
      if (msg.taxDealId !== dealId) return;
      const [update, invalidate] = remoteEditActions(
        msg.taxDealId,
        msg.documentId,
        msg.patch,
      );
      dispatch(update);
      dispatch(invalidate);
    });
  }, [dealId, dispatch]);

  const { data: deal } = useGetTaxDealByIdQuery(
    { id: dealId ?? '' },
    { skip: !dealId },
  );

  const [selectedDoc, setSelectedDoc] = useState<TaxDocumentRecord | null>(
    null,
  );
  const [selectedFYs, setSelectedFYs] = useState<number[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [manageOpen, setManageOpen] = useState(false);

  // Auto-select document from URL param once docs load
  useEffect(() => {
    if (!docs || !initialDocId) return;
    const found = docs.find((d) => d.id === initialDocId);
    if (found) setSelectedDoc(found);
  }, [docs, initialDocId]);

  const availableYears = useMemo(() => {
    if (!docs) return [];
    const years = new Set<number>();
    docs.forEach((d) => {
      if (d.financialYearEnd) years.add(d.financialYearEnd);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [docs]);

  const availableProperties = useMemo(
    () =>
      deal?.properties
        ?.map((p) => p.address)
        .filter((address): address is string => Boolean(address)) ?? [],
    [deal?.properties],
  );

  const filtered = useMemo(() => {
    if (!docs) return [];
    return docs.filter((d) => {
      const fyMatch =
        selectedFYs.length === 0 ||
        (d.financialYearEnd != null &&
          selectedFYs.includes(d.financialYearEnd));
      const propMatch =
        selectedProperties.length === 0 ||
        selectedProperties.some((addr) =>
          matchesProperty(d.extras?.property, addr),
        );
      return fyMatch && propMatch;
    });
  }, [docs, selectedFYs, selectedProperties]);

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Splitter style={{ height: '100%' }}>
        {/* Left: folder tree navigator */}
        <Splitter.Panel
          defaultSize="30%"
          min="240px"
          max="50%"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRight: '1px solid #f0f0f0',
              backgroundColor: '#fff',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '12px 12px 10px',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0,
              }}
            >
              <Space align="baseline" style={{ marginBottom: 8 }}>
                <Text strong style={{ fontSize: 14 }}>
                  Documents
                </Text>
                {docs && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ({docs.length})
                  </Text>
                )}
              </Space>

              {availableYears.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 10, display: 'block', marginBottom: 4 }}
                  >
                    Financial Year
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px 10px',
                      padding: '6px 8px',
                      background: '#fafafa',
                      border: '1px solid #f0f0f0',
                      borderRadius: 6,
                    }}
                  >
                    <Checkbox
                      checked={selectedFYs.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedFYs([]);
                      }}
                      style={{ fontSize: 12 }}
                    >
                      All
                    </Checkbox>
                    {availableYears.map((y) => (
                      <Checkbox
                        key={y}
                        checked={selectedFYs.includes(y)}
                        onChange={(e) =>
                          setSelectedFYs((prev) =>
                            e.target.checked
                              ? [...prev, y]
                              : prev.filter((v) => v !== y),
                          )
                        }
                        style={{ fontSize: 12 }}
                      >
                        FY {y}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              )}

              {availableProperties.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      Property
                    </Text>
                    <Button
                      size="small"
                      type="link"
                      icon={<PartitionOutlined />}
                      onClick={() => setManageOpen(true)}
                      style={{ fontSize: 11, padding: 0, height: 'auto' }}
                    >
                      Manage Properties
                    </Button>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px 10px',
                      padding: '6px 8px',
                      background: '#fafafa',
                      border: '1px solid #f0f0f0',
                      borderRadius: 6,
                    }}
                  >
                    <Checkbox
                      checked={selectedProperties.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedProperties([]);
                      }}
                      style={{ fontSize: 12 }}
                    >
                      All
                    </Checkbox>
                    {availableProperties.map((p) => (
                      <Checkbox
                        key={p}
                        checked={selectedProperties.includes(p)}
                        onChange={(e) =>
                          setSelectedProperties((prev) =>
                            e.target.checked
                              ? [...prev, p]
                              : prev.filter((v) => v !== p),
                          )
                        }
                        style={{ fontSize: 12 }}
                        title={p}
                      >
                        <span title={p}>
                          {p && p.length > 28 ? `${p.slice(0, 28)}…` : p}
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tree */}
            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              {isLoading ? (
                <div style={{ padding: 32, textAlign: 'center' }}>
                  <Spin />
                </div>
              ) : (
                <TaxDocumentExplorer
                  docs={filtered}
                  dealId={dealId}
                  selectedDocId={selectedDoc?.id}
                  onSelectDoc={setSelectedDoc}
                />
              )}
            </div>
          </div>
        </Splitter.Panel>

        {/* Right: preview + extracted data */}
        <Splitter.Panel style={{ overflow: 'hidden' }}>
          {selectedDoc ? (
            <TaxPreviewPanel
              doc={selectedDoc}
              onClose={() => setSelectedDoc(null)}
            />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#525659',
              }}
            >
              <Space direction="vertical" align="center">
                <FileTextOutlined style={{ fontSize: 40, color: '#888' }} />
                <Text style={{ color: '#aaa', fontSize: 14 }}>
                  Select a document to preview
                </Text>
              </Space>
            </div>
          )}
        </Splitter.Panel>
      </Splitter>

      <ManagePropertiesDrawer
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        dealId={dealId}
      />
    </div>
  );
};

export default TaxDocumentTab;
